// OMS Pricing Calculations Service
import { PRICE_PERCENTAGE_DIVISOR, DEFAULT_PAR_VALUE_PRICING, TRANSACTION_TYPES, COMMISSION_TYPES } from '../utils/constants'

export class PricingCalculations {
  // Calculate price with spread applied
  static calculateSpreadAdjustedPrice(basePrice, spread, transactionType, commissionType = COMMISSION_TYPES.MARGIN_SPREAD) {
    if (commissionType === COMMISSION_TYPES.CAPITAL_PRICE_PREMIUM) {
      // In capital-price-premium mode, the price field already stores the adjusted price
      // (which includes the premium), so we return it as-is to avoid double-counting
      return basePrice
    }

    // Margin spread mode - apply basis points adjustment
    if (!spread || spread === 0) return basePrice

    const spreadMultiplier = spread / 10000.0 // Convert basis points to decimal
    return transactionType === TRANSACTION_TYPES.BUY
      ? basePrice * (1 + spreadMultiplier)
      : basePrice * (1 - spreadMultiplier)
  }

  // Calculate principal amount
  static calculatePrincipal(quantity, price, parValuePricing = DEFAULT_PAR_VALUE_PRICING) {
    if (!quantity || !price) return 0
    // Price is per 100, multiply by quantity and par value pricing
    return (quantity * price * parValuePricing) / PRICE_PERCENTAGE_DIVISOR
  }

  // Calculate margin from spread and inventory margin (with market margin fallback)
  static calculateMarginFromSpread(spread, inventoryMargin, transactionType, marketMargin = null) {
    // Use inventory margin if available, otherwise fall back to market margin
    const baseMargin = inventoryMargin !== null && inventoryMargin !== undefined && !isNaN(inventoryMargin) 
      ? inventoryMargin 
      : marketMargin
    
    if (baseMargin === null || baseMargin === undefined || isNaN(baseMargin)) {
      return null
    }
    
    if (transactionType === TRANSACTION_TYPES.BUY) {
      // For buy: margin = inventory_margin - spread
      return baseMargin - spread
    } else {
      // For sell: margin = inventory_margin + spread
      return baseMargin + spread
    }
  }

  // Calculate spread from margin and inventory margin (with market margin fallback)
  static calculateSpreadFromMargin(margin, inventoryMargin, transactionType, marketMargin = null) {
    // Use inventory margin if available, otherwise fall back to market margin
    const baseMargin = inventoryMargin !== null && inventoryMargin !== undefined && !isNaN(inventoryMargin) 
      ? inventoryMargin 
      : marketMargin
    
    if (baseMargin === null || baseMargin === undefined || isNaN(baseMargin)) {
      return null
    }
    
    if (transactionType === TRANSACTION_TYPES.BUY) {
      return baseMargin - margin
    } else {
      return margin - baseMargin
    }
  }

  // Calculate settlement date based on type
  static calculateSettlementDate(settlementType, negotiatedDate = null) {
    if (settlementType === 'negotiated' && negotiatedDate) {
      return negotiatedDate
    }
    
    // Calculate settlement date based on T+n
    const today = new Date()
    const daysToAdd = {
      't_plus_1': 1,
      't_plus_2': 2,
      't_plus_3': 3
    }[settlementType] || 2
    
    today.setDate(today.getDate() + daysToAdd)
    
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')
    
    return `${year}-${month}-${day}`
  }

  // Validate spread against limits
  static validateSpread(spread, min, max) {
    const errors = []

    // Round to 3 decimal places to avoid floating point precision issues
    const roundedSpread = Math.round(spread * 1000) / 1000
    const roundedMin = min !== null ? Math.round(min * 1000) / 1000 : null
    const roundedMax = max !== null ? Math.round(max * 1000) / 1000 : null

    if (roundedMin !== null && roundedSpread < roundedMin) {
      errors.push(`Spread must be at least ${min}`)
    }
    if (roundedMax !== null && roundedSpread > roundedMax) {
      errors.push(`Spread cannot exceed ${max}`)
    }
    return errors
  }
}